import {
  amountInWordsGbp,
  amountInWordsInr,
  computePaymentInvoiceTotals,
  formatGbp,
  formatPaymentInvoiceDate,
  paymentLineAmount,
  PAYMENT_COMPANY,
} from './paymentInvoiceModelDefaults';
import { downloadInvoicePdf } from './invoiceExport';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildLineRowsHtml(model) {
  return (model.lineItems || [])
    .map((item, index) => {
      const days = item.durationDays ? `${escapeHtml(item.durationDays)} days` : '—';
      return `
        <tr>
          <td>${index + 1}.</td>
          <td>${escapeHtml(item.description || '—')}</td>
          <td>${days}</td>
          <td style="text-align:right;">${escapeHtml(item.ratePerDay || '—')}</td>
          <td style="text-align:right;">${formatGbp(paymentLineAmount(item)).replace('£', '')}</td>
        </tr>
      `;
    })
    .join('');
}

export function buildPaymentInvoiceWordHtml(model) {
  const { subtotal, total, inrAmount } = computePaymentInvoiceTotals(model);
  const logoUrl = PAYMENT_COMPANY.logoSrc;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Payment Invoice ${escapeHtml(model.invoiceNumber)}</title>
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; color: #0f172a; margin: 28px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
    .brand { display: flex; align-items: center; gap: 12px; }
    .brand img { height: 52px; }
    .title { font-size: 28px; letter-spacing: 0.14em; font-weight: 700; text-align: right; }
    .meta { font-size: 13px; color: #475569; text-align: right; margin-top: 8px; }
    .addresses { display: table; width: 100%; border: 1px solid #cbd5e1; margin-bottom: 20px; }
    .addresses > div { display: table-cell; width: 50%; padding: 16px; vertical-align: top; }
    .addresses > div:first-child { border-right: 1px solid #cbd5e1; }
    .label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #0d9488; }
    table.items { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    table.items th { background: #d1fae5; padding: 10px; text-align: left; font-size: 12px; border: 1px solid #cbd5e1; }
    table.items td { padding: 10px; border: 1px solid #e2e8f0; font-size: 12px; }
    .totals { width: 220px; margin-left: auto; padding: 12px; }
    .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
    .balance { background: #d1fae5; font-weight: 700; padding: 6px 8px; }
    .words, .bank { margin-top: 18px; padding: 14px; border: 1px solid #cbd5e1; border-radius: 8px; }
    .sign { margin-top: 36px; text-align: right; }
    .sign-line { width: 200px; border-top: 1px solid #94a3b8; margin: 40px 0 8px auto; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <img src="${logoUrl}" alt="Spotless AI" />
      <div>
        <strong>${escapeHtml(model.companyName)}</strong><br />
        <span style="font-size:12px;color:#64748b;">Export &amp; service billing</span>
      </div>
    </div>
    <div>
      <div class="title">INVOICE</div>
      <div class="meta">Date: ${formatPaymentInvoiceDate(model.issueDate)}<br />Invoice No: ${escapeHtml(model.invoiceNumber)}</div>
    </div>
  </div>

  <div class="addresses">
    <div>
      <strong>${escapeHtml(model.companyName)}</strong><br />
      ${escapeHtml(model.companyAddress)}<br />
      Email: ${escapeHtml(model.companyEmail)}<br />
      MOB: ${escapeHtml(model.companyPhone)}<br />
      GSTIN: ${escapeHtml(model.companyGstin)}
    </div>
    <div>
      <div class="label">Bill To</div>
      <strong>${escapeHtml(model.billToName || '—')}</strong><br />
      ${escapeHtml(model.billToAddress || '—')}
    </div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th>No.</th>
        <th>Description</th>
        <th>Duration</th>
        <th style="text-align:right;">Rate (GBP/day)</th>
        <th style="text-align:right;">Amount (GBP)</th>
      </tr>
    </thead>
    <tbody>${buildLineRowsHtml(model)}</tbody>
  </table>

  <div class="totals">
    <div><span>Sub Total</span><span>${formatGbp(subtotal).replace('£', '')}</span></div>
    <div><span>Total</span><span>${formatGbp(total).replace('£', '')}</span></div>
    <div class="balance"><span>Balance Due</span><span>${formatGbp(total).replace('£', '')}</span></div>
  </div>

  <div class="words">
    <div class="label">Amount chargeable (in words)</div>
    <strong>GBP VALUE: ${escapeHtml(amountInWordsGbp(total))}</strong><br />
    ${inrAmount > 0 ? `<strong>INR VALUE: ${escapeHtml(amountInWordsInr(inrAmount))}</strong>` : ''}
  </div>

  <div class="bank">
    <div class="label">Company's Bank Details</div>
    Bank Name: ${escapeHtml(model.bankName)}<br />
    A/c No.: ${escapeHtml(model.accountNumber)}<br />
    Branch &amp; IFS Code: ${escapeHtml(model.branchIfsc)}<br />
    Swift Code: ${escapeHtml(model.swiftCode)}<br />
    AD Code: ${escapeHtml(model.adCode)}<br />
    IE Code: ${escapeHtml(model.ieCode)}
  </div>

  <div class="sign">
    <div class="sign-line"></div>
    <strong>${escapeHtml(model.authorizedSignatory || 'Authorized Signatory')}</strong>
  </div>
</body>
</html>`;
}

export function downloadPaymentInvoiceWord(model, fileName) {
  const htmlContent = buildPaymentInvoiceWordHtml(model);
  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export { downloadInvoicePdf as downloadPaymentInvoicePdf };

export function paymentInvoiceFileBaseName(model) {
  const safeNumber = String(model.invoiceNumber || 'invoice').replace(/[^\w()-]+/g, '_');
  return `Spotless_AI_Payment_Invoice_${safeNumber}`;
}
